import React, { Dispatch, SetStateAction, useCallback } from "react";
import { useParams } from "react-router-dom";

import { reportError } from "@/utils/errors";
import { LanguageCode, supportedLanguageCodes } from "@/utils/i18n";
import { useSessionStorage } from "@/utils/session-storage";

const LanguageContext = React.createContext<
    [LanguageCode | null, Dispatch<SetStateAction<LanguageCode | null>>]
>([null, () => reportError(new Error("LanguageContext not initialized"))]);

export function useSessionLanguageCode() {
    const [contextLanguageCode, setContextLanguageCode] = React.useContext(LanguageContext);
    const [sessionLanguageCode, setSessionLanguageCode] = useSessionStorage<LanguageCode | null>(
        "language-code",
        contextLanguageCode
    );

    const handleSetLanguageCode = useCallback(
        (languageCode: LanguageCode) => {
            setSessionLanguageCode(languageCode);
            setContextLanguageCode(languageCode);
        },
        [setContextLanguageCode, setSessionLanguageCode]
    );

    return [sessionLanguageCode, handleSetLanguageCode] as const;
}

function useUrlLanguageCode() {
    const { languageCode } = useParams();

    return supportedLanguageCodes.includes(languageCode as LanguageCode)
        ? (languageCode as LanguageCode)
        : null;
}

export function useLanguageCode() {
    const urlLanguageCode = useUrlLanguageCode();
    const [sessionLanguageCode] = useSessionLanguageCode();

    return urlLanguageCode ?? sessionLanguageCode;
}

export function useLanguageCodeWithDefault() {
    return useLanguageCode() ?? "en";
}
